import React from "react";
import { useOutletContext, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Reveal, StaggerGroup, StaggerChild } from "@/components/site/Reveal";

const BUILDER_GROUPS = [
    {
        key: "prompt",
        title: "Prompt Engineering Studio",
        icon: "fa-solid fa-wand-magic-sparkles",
        summary:
            "Shape exactly how your AI assistant thinks, speaks, and behaves — with intelligent tooling that makes prompt design effortless.",
        items: [
            {
                name: "System Prompts with AI Prompt Assistant",
                body:
                    "Define your bot's persona, tone, scope, and guardrails. Not a prompt expert? The built-in AI Prompt Assistant helps you write, refine, and optimize your prompts.",
            },
            {
                name: "Timezone Configuration",
                body:
                    "Serve a global audience without confusion — scheduling, business hours, timestamps, and time-sensitive responses stay accurate wherever your users are.",
            },
            {
                name: "Greeting Messages",
                body:
                    "Craft welcoming, on-brand greeting messages that engage users the moment a conversation begins.",
            },
            {
                name: "Inactivity Messages",
                body:
                    "Configure smart inactivity messages that gently re-engage users, offer help, or gracefully close the session.",
            },
        ],
    },
    {
        key: "knowledge",
        title: "Knowledge Base",
        icon: "fa-solid fa-book-open",
        summary:
            "Great AI needs great knowledge. Nyra's Knowledge Base turns your organization's content into the brain of your assistant — accurate, relevant, grounded in your truth.",
        items: [
            {
                name: "File Uploads",
                body:
                    "Upload PDFs, Word files, spreadsheets, FAQs, policies, and product guides. Nyra ingests, processes, and indexes automatically.",
            },
            {
                name: "AI-Powered Integration",
                body:
                    "Connect your knowledge sources so your assistant stays continuously in sync — retrieving the right information at the right moment, grounded in approved, up-to-date knowledge.",
            },
        ],
    },
    {
        key: "analytics",
        title: "Comprehensive Analytics",
        icon: "fa-solid fa-chart-column",
        summary:
            "What gets measured gets improved. Complete visibility into how your AI assistant is performing — so you can optimize continuously and prove ROI confidently.",
        items: [
            {
                name: "Conversation Insights",
                body: "Track conversation volumes, engagement trends, and usage patterns across time.",
            },
            {
                name: "Query Intelligence",
                body: "Understand what users are asking, where the bot excels, and where knowledge gaps exist.",
            },
            {
                name: "Performance Metrics",
                body: "Monitor response quality and resolution effectiveness to keep improving the experience.",
            },
            {
                name: "Actionable Reporting",
                body: "Turn every interaction into actionable insight for product, support, and CX teams.",
            },
        ],
    },
];

const WHY = [
    { title: "No-code Simplicity", body: "Launch a fully functional AI assistant without writing code." },
    { title: "AI-Assisted Setup", body: "The AI Prompt Assistant guides you to best-practice bot behaviour." },
    { title: "Knowledge-Grounded", body: "Your bot, grounded in your knowledge — uploaded or integrated." },
    { title: "Data-Driven", body: "Measure everything with built-in comprehensive analytics." },
    { title: "Enterprise-Ready", body: "Backed by CERF Solutions' enterprise-grade security and infrastructure." },
];

export default function Platform() {
    const { openDemo } = useOutletContext();

    return (
        <div className="bg-warm">
            {/* HERO */}
            <section className="relative overflow-hidden pt-20 pb-16 bg-warm">
                <div className="absolute inset-0 pointer-events-none">
                    <div className="aurora-blob blob-violet drift-a" style={{ top: "-10%", left: "10%", width: 420, height: 420 }} />
                    <div className="aurora-blob blob-amber drift-c" style={{ top: "40%", right: "5%", width: 340, height: 340 }} />
                </div>
                <div className="container-x relative z-10 grid lg:grid-cols-12 gap-10 items-center">
                    <div className="lg:col-span-7">
                        <Reveal>
                            <div className="text-sm text-slate-500 mb-4">The Nyra AI Platform</div>
                        </Reveal>
                        <Reveal delay={0.1}>
                            <h1 className="heading-hero text-5xl sm:text-6xl lg:text-7xl">
                                The Enterprise AI Bot Builder.
                                <br />
                                <span className="muted">Design, train, and deploy in minutes.</span>
                            </h1>
                        </Reveal>
                        <Reveal delay={0.2}>
                            <p className="mt-8 text-slate-600 max-w-2xl text-base md:text-lg leading-relaxed">
                                The Nyra AI Platform is a self-service builder that puts enterprise
                                conversational AI directly in your hands. Configure behaviour, feed knowledge,
                                and measure impact — all from one dashboard, no code required.
                            </p>
                        </Reveal>
                        <Reveal delay={0.3}>
                            <div className="mt-8 flex flex-wrap gap-3">
                                <button onClick={openDemo} className="pill-dark" data-testid="platform-cta-app">
                                    Start building
                                </button>
                                <button onClick={openDemo} className="pill-ghost" data-testid="platform-book-demo">
                                    Book a Demo
                                </button>
                            </div>
                        </Reveal>
                    </div>

                    <Reveal delay={0.3} className="lg:col-span-5">
                        <div className="mesh-frame p-8">
                            <div className="glass-card p-6">
                                <div className="text-xs uppercase tracking-[0.15em] text-slate-500 mb-4">
                                    Everything in one dashboard
                                </div>
                                <ul className="space-y-3 text-sm">
                                    {[
                                        "Prompt Studio with AI Prompt Assistant",
                                        "Knowledge Base — Uploads + AI integrations",
                                        "Comprehensive Analytics dashboard",
                                        "Multi-channel bot deployment",
                                        "Enterprise-grade security & compliance",
                                    ].map((t) => (
                                        <li key={t} className="flex items-center gap-3">
                                            <div className="w-6 h-6 rounded-full grid place-items-center bg-slate-900 text-white shrink-0">
                                                <i className="fa-solid fa-check text-[10px]" />
                                            </div>
                                            <div className="text-slate-800">{t}</div>
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        </div>
                    </Reveal>
                </div>
            </section>

            {/* BUILDER GROUPS */}
            <section className="py-24 md:py-28 bg-warm">
                <div className="container-x">
                    <Reveal>
                        <div className="max-w-3xl mb-14">
                            <div className="text-sm text-slate-500 mb-3">The Nyra AI Bot Builder</div>
                            <h2 className="heading-section text-3xl md:text-5xl">
                                Three pillars.{" "}
                                <span className="text-slate-400">One production-ready assistant.</span>
                            </h2>
                        </div>
                    </Reveal>

                    <div className="space-y-8">
                        {BUILDER_GROUPS.map((g) => (
                            <Reveal key={g.key}>
                                <motion.div
                                    whileHover={{ y: -3 }}
                                    transition={{ duration: 0.3 }}
                                    className="gleap-card p-8 md:p-10"
                                    data-testid={`platform-builder-${g.key}`}
                                >
                                    <div className="grid md:grid-cols-12 gap-8">
                                        <div className="md:col-span-4">
                                            <div className="w-12 h-12 rounded-xl grid place-items-center bg-slate-900 text-white mb-5">
                                                <i className={g.icon} />
                                            </div>
                                            <h3
                                                className="heading-section text-3xl md:text-4xl text-slate-900"
                                            >
                                                {g.title}
                                            </h3>
                                            <p className="mt-3 text-slate-600 text-sm leading-relaxed">
                                                {g.summary}
                                            </p>
                                        </div>
                                        <div className="md:col-span-8">
                                            <div className="grid sm:grid-cols-2 gap-5">
                                                {g.items.map((it) => (
                                                    <div key={it.name} className="border-l-2 border-slate-200 pl-4">
                                                        <div className="text-slate-900 text-sm font-medium">
                                                            {it.name}
                                                        </div>
                                                        <div className="text-slate-600 text-sm mt-1 leading-relaxed">
                                                            {it.body}
                                                        </div>
                                                    </div>
                                                ))}
                                            </div>
                                        </div>
                                    </div>
                                </motion.div>
                            </Reveal>
                        ))}
                    </div>
                </div>
            </section>

            {/* WHY */}
            <section className="py-20 bg-warm">
                <div className="container-x">
                    <Reveal>
                        <div className="max-w-3xl mb-10">
                            <div className="text-sm text-slate-500 mb-3">Why build on Nyra AI?</div>
                            <h2 className="heading-section text-3xl md:text-4xl">
                                Enterprise power. <span className="italic">Self-serve simplicity.</span>
                            </h2>
                        </div>
                    </Reveal>
                    <StaggerGroup className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
                        {WHY.map((w) => (
                            <StaggerChild key={w.title}>
                                <div className="gleap-card p-6 h-full">
                                    <div className="text-slate-900 text-lg font-medium">{w.title}</div>
                                    <p className="mt-2 text-slate-600 text-sm leading-relaxed">{w.body}</p>
                                </div>
                            </StaggerChild>
                        ))}
                    </StaggerGroup>
                </div>
            </section>

            {/* CTA */}
            <section className="py-24 md:py-32 bg-warm">
                <div className="container-x text-center max-w-3xl mx-auto">
                    <Reveal>
                        <h2 className="heading-hero text-4xl sm:text-5xl md:text-6xl">
                            Ready to put AI to work
                            <br />
                            <span className="muted">for your enterprise?</span>
                        </h2>
                    </Reveal>
                    <Reveal delay={0.15}>
                        <div className="mt-10 flex flex-wrap items-center justify-center gap-3">
                            <button onClick={openDemo} className="pill-dark">
                                Start building
                            </button>
                            <button onClick={openDemo} className="pill-ghost">
                                Connect with Sales
                            </button>
                        </div>
                    </Reveal>
                </div>
            </section>
        </div>
    );
}
